import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  EKINDS, KINDS, LIMITS, RAR, cardOutcome, clamp, fmt, fmt2, nextColor, playCounter, sanitizeCardFields, sgn2, sgnR, stackCondition,
  stackText, treeOrder, type CardRule, type CardStack, type Index, type Kind, type Rarity
} from '@balansa/shared';
import { imageUrl } from '../lib/api';
import { SUGG, leafValues, linkProblem, valueFrom } from '../lib/helpers';
import { IconSwap } from '../lib/icons';
import { useApp, useWheel, type CardFields, type Dialog, type ImportResult } from '../store';
import { Cn } from './Cn';
import { GameCard } from './GameCard';
import { McpDialog } from './McpDialog';
import { ProfileDialog } from './ProfileDialog';
import { Modal } from './Modal';

export function Dialogs() {
  const d = useApp((s) => s.dialog);
  if (!d) return null;
  // Atslēga nodrošina svaigu formas stāvokli katrai atvēršanai.
  const key = JSON.stringify(d);
  switch (d.kind) {
    case 'node':
      return <NodeDialog key={key} d={d} />;
    case 'link':
      return <LinkDialog key={key} d={d} />;
    case 'play':
      return <PlayDialog key={key} cardId={d.cardId} />;
    case 'card':
      return <CardEditor key={key} cardId={d.cardId} />;
    case 'import':
      return <ImportDialog />;
    case 'mcp':
      return <McpDialog />;
    case 'profile':
      return <ProfileDialog />;
  }
}

/** Jomu izvēlnes opcijas koka secībā ar atkāpēm. */
function NodeOptions({ idx, exclude }: { idx: Index; exclude?: Set<string> }) {
  return (
    <>
      {treeOrder(idx, exclude).map((id) => (
        <option key={id} value={id}>
          {'   '.repeat(idx.depth.get(id)!) + idx.node.get(id)!.name}
        </option>
      ))}
    </>
  );
}

// ---------------------------------------------------------------- joma

function NodeDialog({ d }: { d: Extract<Dialog, { kind: 'node' }> }) {
  const { idx, wheel } = useWheel();
  const A = useApp.getState;
  const isNew = d.mode === 'new';
  const n = d.mode === 'edit' ? idx.node.get(d.id) : undefined;
  const [name, setName] = useState(n?.name ?? '');
  const [parent, setParent] = useState<string>(
    d.mode === 'new' ? d.parentId ?? '' : n?.parentId && idx.node.has(n.parentId) ? n.parentId : ''
  );
  const [color, setColor] = useState(n && !n.parentId ? idx.color(n.id) : nextColor(idx));
  const [note, setNote] = useState(n?.note ?? '');
  const [busy, setBusy] = useState(false);
  const close = () => A().closeDialog();
  if (!isNew && !n) return null;
  const excl = n ? new Set(Array.from(idx.node.keys()).filter((x) => idx.anc.get(x)!.includes(n.id))) : undefined;

  return (
    <Modal onClose={close} label="Joma" testId="node-dialog">
      <form
        className="dlg"
        autoComplete="off"
        onSubmit={async (e) => {
          e.preventDefault();
          const nm = name.trim();
          if (!nm) return;
          setBusy(true);
          const ok =
            d.mode === 'new'
              ? !!(await A().createNode({ name: nm, parentId: parent || null, color: parent ? null : color, note: note.trim() }))
              : await A().updateNode(d.id, { name: nm, note: note.trim(), parentId: parent || null, ...(parent ? {} : { color }) });
          setBusy(false);
          if (ok) close();
        }}
      >
        <h3>{isNew ? (parent ? 'Jauna apakšjoma' : 'Jauna joma') : 'Rediģēt jomu'}</h3>
        <div className="field">
          <label htmlFor="f-name">Nosaukums</label>
          <input className="inp" id="f-name" required maxLength={LIMITS.nodeName} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label htmlFor="f-parent">Atrodas zem</label>
          <select className="inp" id="f-parent" value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="">— galvenā joma (uz apļa) —</option>
            <NodeOptions idx={idx} exclude={excl} />
          </select>
        </div>
        {!parent ? (
          <div className="field">
            <label htmlFor="f-color">Krāsa uz apļa</label>
            <input type="color" id="f-color" value={color} onChange={(e) => setColor(e.target.value)} />
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="f-note">Piezīme / nākamais solis</label>
          <textarea className="inp" id="f-note" rows={3} maxLength={LIMITS.note} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {!isNew ? (
          <div className="field">
            <span className="lbl-t">Secība starp blakus jomām</span>
            <div className="dlg-row">
              <button type="button" className="btn sm" onClick={() => void A().moveNode(n!.id, -1)}>
                ↑ Augstāk
              </button>
              <button type="button" className="btn sm" onClick={() => void A().moveNode(n!.id, 1)}>
                ↓ Zemāk
              </button>
            </div>
          </div>
        ) : null}
        <div className="dlg-actions">
          {!isNew ? (
            <button
              type="button"
              className="btn sm ghost danger"
              onClick={async () => {
                const sub = excl!.size;
                const nl = wheel.links.filter((l) => excl!.has(l.fromId) || excl!.has(l.toId)).length;
                const ok = await A().ask({
                  title: 'Dzēst “' + n!.name + '”' + (sub > 1 ? ' un ' + (sub - 1) + ' apakšjomu(-as)' : '') + '?',
                  message: nl ? 'Tiks dzēstas arī ' + nl + ' saistītās mijiedarbības un kāršu ietekmes uz šīm jomām.' : 'Kāršu ietekmes uz šīm jomām tiks noņemtas.',
                  ok: 'Dzēst',
                  danger: true
                });
                if (ok && (await A().deleteNode(n!.id))) close();
              }}
            >
              Dzēst
            </button>
          ) : null}
          <span className="sp" />
          <button type="button" className="btn sm" onClick={close}>
            Atcelt
          </button>
          <button type="submit" className="btn sm primary" disabled={busy}>
            Saglabāt
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- saite

function LinkDialog({ d }: { d: Extract<Dialog, { kind: 'link' }> }) {
  const { idx, wheel } = useWheel();
  const A = useApp.getState;
  const init = useMemo(() => {
    let l = d.id ? wheel.links.find((x) => x.id === d.id) ?? null : null;
    let note = '';
    if (!l && d.from && d.to) {
      const ex = wheel.links.find((x) => x.fromId === d.from && x.toId === d.to);
      if (ex) {
        l = ex;
        note = 'Šāda saite jau ir — vari to labot.';
      }
    }
    return { l, note };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const l = init.l;
  const [from, setFrom] = useState(l ? l.fromId : d.from && idx.node.has(d.from) ? d.from : '');
  const [to, setTo] = useState(l ? l.toId : d.to && idx.node.has(d.to) ? d.to : '');
  const [type, setType] = useState<'give' | 'take'>(l ? l.type : 'give');
  const [strength, setStrength] = useState<1 | 2 | 3>(l ? l.strength : 2);
  const [label, setLabel] = useState(l ? l.label : '');
  const [err, setErr] = useState('');
  const labelRef = useRef<HTMLInputElement>(null);
  const close = () => A().closeDialog();

  const used = wheel.links.filter((x) => x.type === type && x.label).map((x) => x.label.trim().toLowerCase());
  const sugg = Array.from(new Set(used.concat(SUGG[type]))).slice(0, 10);
  const pr = linkProblem(idx, from, to);
  let preview: ReactNode;
  if (err) preview = err;
  else if (!from || !to) preview = 'Piemēram: Darbs dod naudu → Hobiji';
  else if (pr) preview = pr;
  else
    preview = (
      <>
        <Cn idx={idx} id={from} /> {type === 'give' ? 'dod' : <span className="bad">atņem</span>}
        {label.trim() ? (
          <>
            {' '}
            <b>{label.trim()}</b>
          </>
        ) : null}{' '}
        → <Cn idx={idx} id={to} />
      </>
    );
  const isErr = !!err || (!!from && !!to && !!pr);

  return (
    <Modal onClose={close} label="Saite" testId="link-dialog">
      <form
        className="dlg"
        autoComplete="off"
        onSubmit={async (e) => {
          e.preventDefault();
          if (pr) {
            setErr(pr);
            return;
          }
          const ok = await A().saveLink(l ? l.id : null, { fromId: from, toId: to, type, label: label.trim(), strength });
          if (ok) close();
        }}
      >
        <h3>{l ? 'Labot saiti' : 'Jauna saite'}</h3>
        {init.note ? <p className="ld-note">{init.note}</p> : null}
        <div className="ld-ends">
          <div className="field">
            <label htmlFor="ld-from">No jomas</label>
            <select className="inp" id="ld-from" value={from} autoFocus={!from} onChange={(e) => (setFrom(e.target.value), setErr(''))}>
              <option value="">— izvēlies —</option>
              <NodeOptions idx={idx} />
            </select>
          </div>
          <button
            type="button"
            className="ld-swap"
            title="Apmainīt virzienu"
            aria-label="Apmainīt virzienu"
            onClick={() => {
              setFrom(to);
              setTo(from);
              setErr('');
            }}
          >
            <IconSwap />
          </button>
          <div className="field">
            <label htmlFor="ld-to">Uz jomu</label>
            <select className="inp" id="ld-to" value={to} autoFocus={!!from && !to} onChange={(e) => (setTo(e.target.value), setErr(''))}>
              <option value="">— izvēlies —</option>
              <NodeOptions idx={idx} />
            </select>
          </div>
        </div>
        <div className="lf-row">
          <div className="field">
            <span className="lbl-t">Darbība</span>
            <div className="seg" id="ld-type">
              {(['give', 'take'] as const).map((t) => (
                <button key={t} type="button" data-type={t} aria-pressed={type === t} onClick={() => setType(t)}>
                  {t === 'give' ? 'dod' : 'atņem'}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="lbl-t">Stiprums</span>
            <div className="seg">
              {([1, 2, 3] as const).map((s) => (
                <button key={s} type="button" aria-pressed={strength === s} onClick={() => setStrength(s)}>
                  {['vāja', 'vidēja', 'spēcīga'][s - 1]}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="field">
          <label htmlFor="ld-label">Ko tieši?</label>
          <input
            ref={labelRef}
            className="inp"
            id="ld-label"
            placeholder="piem. naudu, enerģiju, laiku"
            maxLength={LIMITS.linkLabel}
            value={label}
            autoFocus={!!from && !!to}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div className="sugg">
            {sugg.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setLabel(t);
                  labelRef.current?.focus();
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <p className={'lf-preview' + (isErr ? ' err' : '')}>{preview}</p>
        <div className="dlg-actions">
          {l ? (
            <button
              type="button"
              className="btn sm ghost danger"
              onClick={async () => {
                await A().deleteLink(l.id);
                close();
              }}
            >
              Dzēst saiti
            </button>
          ) : null}
          <span className="sp" />
          <button type="button" className="btn sm" onClick={close}>
            Atcelt
          </button>
          <button type="submit" className="btn sm primary">
            {l ? 'Saglabāt' : 'Pievienot'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- izspēle

function PlayDialog({ cardId }: { cardId: string }) {
  const { wheel, idx, en } = useWheel();
  const A = useApp.getState;
  const [note, setNote] = useState('');
  const c = wheel.cards.find((x) => x.id === cardId);
  const close = () => A().closeDialog();
  if (!c) return null;
  const prior = playCounter(wheel.journal, c.id, Date.now());
  const o = cardOutcome(c, idx, en, prior);
  const lm = leafValues(idx);
  o.effects.forEach((e) => idx.leaves.get(e.target)!.forEach((l) => lm.set(l, clamp((lm.get(l) ?? 0) + e.delta, 0, 10))));
  const why: ReactNode[] = ['kārts ' + sgnR(c.energy)];
  if (o.res < 0.999) why.push('noturība (' + EKINDS[c.kind] + ') ×' + fmt2(o.res));
  if (o.eMul !== 1) why.push('nosacījumi ×' + fmt2(o.eMul));
  if (o.stackEnergy) why.push('krāšanās ' + sgnR(o.stackEnergy));
  if (o.L + o.energyDelta > (en.M.cap ?? 100) + 0.5) why.push('ietilpība ' + Math.round(en.M.cap) + ' v. — daļa uzlādes netiks izmantota');
  if (o.L + o.energyDelta < 0)
    why.push(
      <span key="x" className="bad">
        nepietiek enerģijas — baterija būs tukša
      </span>
    );
  const metricNow = (m: string) => (m === 'energy' ? Math.round(en.L) + ' v.' : idx.node.has(m) ? fmt2(idx.val.get(m)!) : '—');
  const play = () => {
    close();
    void A().playCard(c.id, note.trim());
  };

  return (
    <Modal onClose={close} className="wide" label={'Izspēlēt kārti ' + c.name} testId="play-dialog">
      <div className="play">
        <div className="play-card">
          <GameCard card={c} idx={idx} en={en} live eager prior={prior} number={wheel.cards.indexOf(c) + 1} />
        </div>
        <div className="play-side">
          <h3>{c.name}</h3>
          <p className="muted">Ietekme aprēķināta pēc tava stāvokļa tieši tagad.</p>
          <div>
            <div className="po-sec">Enerģija</div>
            <div className="po-row">
              <span>Baterija</span>
              <span className="po-v">
                {Math.round(o.L)} → <b>{Math.round(o.Lafter)}</b> no {Math.round(en.M.cap)} v.
              </span>
              <b className={'po-d ' + (o.energyDelta >= 0 ? 'pos' : 'neg')} data-testid="play-energy">
                {sgnR(o.energyDelta)}
              </b>
            </div>
            <div className="po-why">
              {why.map((w, i) => (
                <span key={i}>
                  {i ? ' · ' : ''}
                  {w}
                </span>
              ))}
            </div>
            {o.effects.length ? (
              <>
                <div className="po-sec">
                  Jomas{o.aMul !== 1 ? <span className="po-mul">ietekme ×{fmt2(o.aMul)}</span> : null}
                </div>
                {o.effects.map((e, i) => (
                  <div className="po-row" key={i}>
                    <span>
                      <Cn idx={idx} id={e.target} />
                    </span>
                    <span className="po-v">
                      {fmt2(idx.val.get(e.target)!)} → <b>{fmt2(valueFrom(idx, e.target, lm))}</b>
                    </span>
                    <b className={'po-d ' + (e.delta >= 0 ? 'pos' : 'neg')}>{sgn2(e.delta)}</b>
                  </div>
                ))}
              </>
            ) : null}
            {c.rules.length ? (
              <>
                <div className="po-sec">Nosacījumi</div>
                {c.rules.map((r, i) => {
                  const on = o.active.includes(i);
                  return (
                    <div key={i} className={'po-rule' + (on ? ' on' : '')}>
                      <span>{on ? '✓' : '○'}</span>
                      <div>
                        <RuleLine r={r} idx={idx} /> <em>(tagad {metricNow(r.metric)})</em>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : null}
            {c.stacks.length ? (
              <>
                <div className="po-sec">Krāšanās (atkārtošana)</div>
                {c.stacks.map((s, i) => {
                  const st = o.stacks[i];
                  return (
                    <div key={i} className={'po-rule' + (st.active ? ' on' : '')} data-testid={'play-stack-' + i}>
                      <span>{st.active ? '✓' : '○'}</span>
                      <div>
                        {stackText(s, idx)}{' '}
                        <em>
                          {st.active
                            ? `(šī būs ${st.have}. izspēle ${s.days} d. laikā)`
                            : `(līdz šim ${st.have - 1}× pēdējās ${s.days} d.; bonuss no ${s.count}. izspēles)`}
                        </em>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>
          <label className="field">
            <span className="lbl-t">Piezīme (neobligāti)</span>
            <input
              className="inp"
              maxLength={80}
              placeholder="piem. 5 km skrējiens"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  play();
                }
              }}
            />
          </label>
          <div className="dlg-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn sm ghost" onClick={() => A().openDialog({ kind: 'card', cardId: c.id })}>
              Rediģēt kārti
            </button>
            <span className="sp" />
            <button type="button" className="btn sm" onClick={close}>
              Atcelt
            </button>
            <button type="button" className="btn sm primary" onClick={play} autoFocus data-testid="play-go">
              Izspēlēt kārti
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function RuleLine({ r, idx }: { r: CardRule; idx: Index }) {
  const parts: string[] = [];
  if (r.energyMul !== 1) parts.push('enerģija ×' + fmt2(r.energyMul));
  if (r.areaMul !== 1) parts.push('ietekme ×' + fmt2(r.areaMul));
  const m = r.metric === 'energy' ? 'enerģija' : idx.node.has(r.metric) ? idx.node.get(r.metric)!.name : '(dzēsta joma)';
  return (
    <>
      Ja {m} {r.op === 'lt' ? '<' : '>'} {fmt2(r.value)}: {parts.join(', ') || 'bez izmaiņām'}
    </>
  );
}

// ---------------------------------------------------------------- kārts redaktors

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_MB = 8;

/** Skaitļa lauks, kas ļauj ievadīt starpstāvokļus (“-”, “0,”) un saglabā tikai derīgu skaitli. */
function NumInput(p: { value: number; onChange: (v: number) => void; step: number; min: number; max: number; label: string }) {
  const [s, setS] = useState<string | null>(null);
  return (
    <input
      className="inp num"
      type="number"
      step={p.step}
      min={p.min}
      max={p.max}
      aria-label={p.label}
      value={s ?? String(p.value)}
      onChange={(e) => {
        setS(e.target.value);
        const v = parseFloat(e.target.value.replace(',', '.'));
        if (Number.isFinite(v)) p.onChange(v);
      }}
      onBlur={() => setS(null)}
    />
  );
}

function CardEditor({ cardId }: { cardId: string | null }) {
  const { wheel, idx, en } = useWheel();
  const selected = useApp((s) => s.ui.selected);
  const A = useApp.getState;
  const c = cardId ? wheel.cards.find((x) => x.id === cardId) ?? null : null;
  const [draft, setDraft] = useState<CardFields>(() =>
    c
      ? {
          name: c.name, typeId: c.typeId, rarity: c.rarity, flavor: c.flavor, energy: c.energy, kind: c.kind,
          effects: c.effects.map((e) => ({ ...e })),
          rules: c.rules.map((r) => ({ ...r })),
          stacks: c.stacks.map((s) => ({ ...s, effects: s.effects.map((e) => ({ ...e })) }))
        }
      : {
          name: '',
          typeId: selected && idx.node.has(selected) ? idx.top.get(selected)! : null,
          rarity: 'c',
          flavor: '',
          energy: -10,
          kind: 'fiz',
          effects: [],
          rules: [],
          stacks: []
        }
  );
  const [file, setFile] = useState<File | null>(null);
  const [blob, setBlob] = useState<string | null>(null);
  const [removeImg, setRemoveImg] = useState(false);
  const [imgErr, setImgErr] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const close = () => A().closeDialog();

  useEffect(() => () => void (blob && URL.revokeObjectURL(blob)), [blob]);

  const upd = (p: Partial<CardFields>) => setDraft((d) => ({ ...d, ...p }));
  const nodeIds = new Set(idx.node.keys());
  const clean = sanitizeCardFields(draft, nodeIds);
  const hasImage = !!file || (!!c?.image && !removeImg);
  const imgSrc = blob ?? (c?.image && !removeImg ? imageUrl(c.image) : null);
  const previewCard = { ...clean, name: draft.name, image: hasImage ? 'x' : null };
  const prior = c ? playCounter(wheel.journal, c.id, Date.now()) : undefined;
  const o = cardOutcome({ ...clean, id: c?.id ?? 'x', image: null, sortOrder: 0 }, idx, en, prior);
  const r = en.M.remain[draft.kind] || 1;
  const v = draft.energy;
  const fields = (): CardFields => ({ ...clean, name: draft.name.trim() });

  const onPick = (f: File | undefined) => {
    setImgErr('');
    if (!f) return;
    if (!IMAGE_TYPES.includes(f.type)) return setImgErr('Atbalstītie formāti: JPG, PNG, WebP, GIF.');
    if (f.size > MAX_MB * 1024 * 1024) return setImgErr(`Attēls ir lielāks par ${MAX_MB} MB.`);
    setFile(f);
    setRemoveImg(false);
    setBlob(URL.createObjectURL(f));
  };

  const setRule = (i: number, p: Partial<CardRule>) => upd({ rules: draft.rules.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const setStack = (i: number, p: Partial<CardStack>) => upd({ stacks: draft.stacks.map((x, j) => (j === i ? { ...x, ...p } : x)) });

  return (
    <Modal onClose={close} className="wide" label="Kārts redaktors" testId="card-dialog">
      <form
        className="cedit"
        autoComplete="off"
        // Bez pārlūka “step” validācijas: kārtis no MCP var saturēt jebkuras vērtības (piem. ×1,25); robežas apgriež sanitizācija.
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!draft.name.trim()) {
            (e.currentTarget.querySelector('#cd-name') as HTMLInputElement | null)?.focus();
            return;
          }
          setBusy(true);
          const ok = c ? await A().updateCard(c.id, fields(), file, removeImg) : !!(await A().createCard(fields(), file));
          setBusy(false);
          if (ok) close();
        }}
      >
        <div className="cedit-form">
          <h3 className="cd-h">{c ? 'Rediģēt kārti' : 'Jauna kārts'}</h3>
          <div className="cd-grid">
            <div className="field cd-full">
              <label htmlFor="cd-name">Nosaukums</label>
              <input className="inp" id="cd-name" required maxLength={LIMITS.cardName} value={draft.name} onChange={(e) => upd({ name: e.target.value })} autoFocus />
            </div>
            <div className="field">
              <label htmlFor="cd-type">Joma (kārts krāsa)</label>
              <select className="inp" id="cd-type" value={draft.typeId ?? ''} onChange={(e) => upd({ typeId: e.target.value || null })}>
                <option value="">— vispārēja —</option>
                {idx.tops.map((t) => (
                  <option key={t} value={t}>
                    {idx.node.get(t)!.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cd-rar">Retums</label>
              <select className="inp" id="cd-rar" value={draft.rarity} onChange={(e) => upd({ rarity: e.target.value as Rarity })}>
                {(Object.keys(RAR) as Rarity[]).map((k) => (
                  <option key={k} value={k}>
                    {RAR[k][1]} {RAR[k][0]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field cd-full">
              <span className="lbl-t">Attēls</span>
              <div className="cd-img">
                <button type="button" className="btn sm" onClick={() => fileRef.current?.click()}>
                  {hasImage ? 'Mainīt attēlu…' : 'Izvēlēties attēlu…'}
                </button>
                {hasImage ? (
                  <button
                    type="button"
                    className="btn sm ghost danger"
                    onClick={() => {
                      setFile(null);
                      setBlob(null);
                      setRemoveImg(true);
                    }}
                  >
                    Noņemt
                  </button>
                ) : null}
                <span className="cd-file">
                  {file ? (
                    file.name
                  ) : c?.image && !removeImg ? (
                    <>
                      saglabāts serverī ·{' '}
                      <a href={imageUrl(c.image, 'original')} target="_blank" rel="noreferrer" data-testid="card-image-original">
                        oriģināls
                      </a>
                    </>
                  ) : (
                    ''
                  )}
                </span>
                <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} hidden data-testid="card-image-input" onChange={(e) => (onPick(e.target.files?.[0]), (e.target.value = ''))} />
              </div>
              {imgErr ? <p className="cd-hint bad">{imgErr}</p> : null}
              <p className="cd-hint">
                Oriģināls tiek saglabāts serverī nemainīts, kārtīs rāda saspiestu sīktēlu (WebP, līdz 720 px). Ieteicamais izmērs ~650 × 450–500 px vai lielāks; JPG, PNG, WebP vai GIF līdz {MAX_MB} MB.
              </p>
            </div>
            <div className="field cd-full">
              <label htmlFor="cd-flavor">Apraksts kārts apakšā</label>
              <input className="inp" id="cd-flavor" maxLength={LIMITS.flavor} placeholder="piem. Sviedri šodien — spēks rīt." value={draft.flavor} onChange={(e) => upd({ flavor: e.target.value })} />
            </div>
          </div>

          <div className="cd-sec">Enerģija</div>
          <div className="cd-energy">
            <input
              type="range"
              className="rng"
              min={-60}
              max={60}
              step={1}
              value={draft.energy}
              aria-label="Enerģijas izmaiņa"
              style={{ '--p': (((v + 60) / 120) * 100).toFixed(1) + '%', '--c': v < 0 ? 'var(--danger)' : 'var(--good)' } as React.CSSProperties}
              onChange={(e) => upd({ energy: +e.target.value })}
            />
            <b>{sgnR(v)} v.</b>
            <select className="inp" aria-label="Slodzes veids" value={draft.kind} disabled={v >= 0} onChange={(e) => upd({ kind: e.target.value as Kind })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {EKINDS[k]} slodze
                </option>
              ))}
            </select>
          </div>
          <p className="cd-hint">
            {v < 0
              ? 'Patērē enerģiju (vienības). Noturība no enerģijas modeļa pašlaik samazina ' + EKINDS[draft.kind] + 's slodzes izmaksas par ' + Math.round((1 - r) * 100) + '%.'
              : v > 15
                ? 'Liela uzlāde — aktivitātes pārsvarā patērē enerģiju; galvenie avoti ir miegs un ēdiens. Virs +15 parasti neder pat miega veida kārtīm.'
                : v > 5
                  ? 'Uzlādē (miega veida aktivitātei +5…+15). Ikdienas uzlādējošas aktivitātes (kafija, duša) parasti dod tikai +1…+5.'
                  : v > 0
                    ? 'Neliela uzlāde (baterija nepārsniegs ietilpību).'
                    : 'Enerģiju neietekmē.'}
          </p>

          <div className="cd-sec">Ietekme uz jomām</div>
          {draft.effects.length ? (
            draft.effects.map((e, i) => (
              <div className="cd-row" key={i}>
                <select
                  className="inp"
                  aria-label="Joma"
                  value={e.target}
                  onChange={(ev) => upd({ effects: draft.effects.map((x, j) => (j === i ? { ...x, target: ev.target.value } : x)) })}
                >
                  <NodeOptions idx={idx} />
                </select>
                <NumInput
                  value={e.delta}
                  step={0.01}
                  min={-3}
                  max={3}
                  label="Izmaiņa"
                  onChange={(d) => upd({ effects: draft.effects.map((x, j) => (j === i ? { ...x, delta: d } : x)) })}
                />
                <button type="button" className="icon-btn" title="Noņemt" aria-label="Noņemt" onClick={() => upd({ effects: draft.effects.filter((_, j) => j !== i) })}>
                  ×
                </button>
              </div>
            ))
          ) : (
            <p className="cd-hint" style={{ margin: '0 0 6px' }}>
              Kārts neietekmē jomas.
            </p>
          )}
          <button
            type="button"
            className="btn sm"
            disabled={draft.effects.length >= LIMITS.cardEffects || !idx.tops.length}
            onClick={() => upd({ effects: [...draft.effects, { target: draft.typeId || idx.tops[0], delta: 0.2 }] })}
          >
            + Pievienot ietekmi
          </button>
          <p className="cd-hint">Izmaiņa −3…+3 ar precizitāti 0,01. Ja izvēlas virsjomu, izmaiņa tiek pievienota katrai tās apakšjomai.</p>

          <div className="cd-sec">Nosacījumi — ietekme atkarībā no stāvokļa</div>
          {draft.rules.length ? (
            draft.rules.map((rr, i) => (
              <div className="cd-row cd-rule" key={i}>
                <div className="cd-line">
                  <span>Ja</span>
                  <select
                    className="inp"
                    aria-label="Rādītājs"
                    value={rr.metric}
                    onChange={(ev) => {
                      const m = ev.target.value;
                      const wasE = rr.metric === 'energy';
                      setRule(i, { metric: m, ...(wasE !== (m === 'energy') ? { value: m === 'energy' ? 30 : 4 } : {}) });
                    }}
                  >
                    <option value="energy">Enerģija (v.)</option>
                    <NodeOptions idx={idx} />
                  </select>
                  <select className="inp op" aria-label="Salīdzinājums" value={rr.op} onChange={(ev) => setRule(i, { op: ev.target.value as 'lt' | 'gt' })}>
                    <option value="lt">&lt;</option>
                    <option value="gt">&gt;</option>
                  </select>
                  <NumInput
                    key={rr.metric === 'energy' ? 'e' : 'n'}
                    value={rr.value}
                    step={rr.metric === 'energy' ? 1 : 0.01}
                    min={0}
                    max={rr.metric === 'energy' ? 300 : 10}
                    label="Robeža"
                    onChange={(x) => setRule(i, { value: x })}
                  />
                  <button type="button" className="icon-btn" title="Noņemt" aria-label="Noņemt" onClick={() => upd({ rules: draft.rules.filter((_, j) => j !== i) })}>
                    ×
                  </button>
                </div>
                <div className="cd-line">
                  <span>tad enerģija ×</span>
                  <NumInput value={rr.energyMul} step={0.01} min={0} max={5} label="Enerģijas reizinātājs" onChange={(x) => setRule(i, { energyMul: x })} />
                  <span>· jomu ietekme ×</span>
                  <NumInput value={rr.areaMul} step={0.01} min={0} max={5} label="Jomu ietekmes reizinātājs" onChange={(x) => setRule(i, { areaMul: x })} />
                </div>
              </div>
            ))
          ) : (
            <p className="cd-hint" style={{ margin: '0 0 6px' }}>
              Nav nosacījumu — kārts vienmēr darbojas vienādi.
            </p>
          )}
          <button
            type="button"
            className="btn sm"
            disabled={draft.rules.length >= LIMITS.cardRules}
            onClick={() => upd({ rules: [...draft.rules, { metric: 'energy', op: 'lt', value: 30, energyMul: 1.5, areaMul: 0.5 }] })}
          >
            + Pievienot nosacījumu
          </button>
          <p className="cd-hint">Nosacījums pārbauda stāvokli izspēles brīdī. Vairāki aktīvi nosacījumi reizinās.</p>

          <div className="cd-sec">Krāšanās — papildu efekts, ja kārts bieži izspēlēta</div>
          {draft.stacks.length ? (
            draft.stacks.map((s, i) => (
              <div className="cd-row cd-rule cd-stack" key={i} data-testid={'stack-row-' + i}>
                <div className="cd-line">
                  <input
                    className="inp cd-stack-label"
                    aria-label="Bonusa nosaukums"
                    placeholder="nosaukums, piem. Ieradums"
                    maxLength={LIMITS.stackLabel}
                    value={s.label}
                    onChange={(ev) => setStack(i, { label: ev.target.value })}
                  />
                  <button type="button" className="icon-btn" title="Noņemt bonusu" aria-label="Noņemt bonusu" onClick={() => upd({ stacks: draft.stacks.filter((_, j) => j !== i) })}>
                    ×
                  </button>
                </div>
                <div className="cd-line">
                  <span>Ja izspēlēta vismaz</span>
                  <NumInput value={s.count} step={1} min={2} max={20} label="Izspēļu skaits" onChange={(x) => setStack(i, { count: Math.round(x) })} />
                  <span>reizes pēdējās</span>
                  <NumInput value={s.days} step={1} min={1} max={90} label="Dienu skaits" onChange={(x) => setStack(i, { days: Math.round(x) })} />
                  <span>dienās (ieskaitot šo)</span>
                </div>
                <div className="cd-line">
                  <span>tad papildus enerģija</span>
                  <NumInput value={s.energy} step={1} min={-30} max={30} label="Papildu enerģija" onChange={(x) => setStack(i, { energy: Math.round(x) })} />
                  <span>v.</span>
                </div>
                {s.effects.map((e, k) => (
                  <div className="cd-line" key={k}>
                    <span>un</span>
                    <select
                      className="inp"
                      aria-label="Bonusa joma"
                      value={e.target}
                      onChange={(ev) => setStack(i, { effects: s.effects.map((x, j) => (j === k ? { ...x, target: ev.target.value } : x)) })}
                    >
                      <NodeOptions idx={idx} />
                    </select>
                    <NumInput
                      value={e.delta}
                      step={0.01}
                      min={-3}
                      max={3}
                      label="Bonusa izmaiņa"
                      onChange={(d) => setStack(i, { effects: s.effects.map((x, j) => (j === k ? { ...x, delta: d } : x)) })}
                    />
                    <button type="button" className="icon-btn" title="Noņemt" aria-label="Noņemt bonusa ietekmi" onClick={() => setStack(i, { effects: s.effects.filter((_, j) => j !== k) })}>
                      ×
                    </button>
                  </div>
                ))}
                <div className="cd-line">
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={s.effects.length >= LIMITS.stackEffects || !idx.tops.length}
                    onClick={() => setStack(i, { effects: [...s.effects, { target: draft.typeId || idx.tops[0], delta: 0.2 }] })}
                  >
                    + ietekme uz jomu
                  </button>
                  {prior && o.stacks[i] && clean.stacks.length === draft.stacks.length ? (
                    <span className="cd-hint" style={{ margin: 0 }}>
                      {o.stacks[i].active ? 'Nākamā izspēle saņems bonusu.' : `Pēdējās ${s.days} d.: ${o.stacks[i].have - 1}× (${stackCondition(s)})`}
                    </span>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <p className="cd-hint" style={{ margin: '0 0 6px' }}>
              Nav krāšanās bonusu — atkārtošana efektu nemaina.
            </p>
          )}
          <button
            type="button"
            className="btn sm"
            data-testid="add-stack"
            disabled={draft.stacks.length >= LIMITS.cardStacks || !idx.tops.length}
            onClick={() =>
              upd({ stacks: [...draft.stacks, { count: 3, days: 7, energy: 0, effects: [{ target: draft.typeId || idx.tops[0], delta: 0.2 }], label: 'Ieradums' }] })
            }
          >
            + Pievienot krāšanās bonusu
          </button>
          <p className="cd-hint">
            Piemēram, “Ieradums”: ja sports 7 dienās izspēlēts 3 reizes, 3. un nākamās izspēles nogurdina mazāk un dod vairāk; “Pārslodze” — ar negatīvām
            vērtībām. Bonuss netiek reizināts ar nosacījumiem; vairāki bonusi summējas. Bonuss bez enerģijas un ietekmes netiek saglabāts.
          </p>

          <div className="dlg-actions" style={{ marginTop: 18 }}>
            {c ? (
              <>
                <button
                  type="button"
                  className="btn sm ghost danger"
                  onClick={async () => {
                    const ok = await A().ask({ title: 'Dzēst kārti “' + c.name + '”?', message: 'Žurnāla ieraksti saglabāsies.', ok: 'Dzēst', danger: true });
                    if (ok && (await A().deleteCard(c.id))) close();
                  }}
                >
                  Dzēst
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={async () => {
                    if (!draft.name.trim()) return;
                    setBusy(true);
                    const id = await A().duplicateCard(c.id, fields(), file);
                    setBusy(false);
                    if (id) close();
                  }}
                >
                  Dublēt
                </button>
              </>
            ) : null}
            <span className="sp" />
            <button type="button" className="btn sm" onClick={close}>
              Atcelt
            </button>
            <button type="submit" className="btn sm primary" disabled={busy} data-testid="card-save">
              Saglabāt kārti
            </button>
          </div>
        </div>
        <div className="cedit-prev">
          <div className="cd-sec" style={{ marginTop: 0 }}>
            Priekšskatījums
          </div>
          <GameCard card={previewCard} idx={idx} en={en} live eager prior={prior} imgSrc={imgSrc} number={c ? wheel.cards.indexOf(c) + 1 : wheel.cards.length + 1} />
          <p className="cd-hint">
            Ja izspēlētu tagad: ⚡ {sgnR(o.energyDelta)} v.{o.aMul !== 1 ? ' · ietekme ×' + fmt2(o.aMul) : ''}
            {o.active.length ? ' · aktīvi nosacījumi: ' + o.active.length : ''}
            {o.stackEnergy || o.stacks.some((s) => s.active) ? ' · krāšanās bonuss aktīvs' : ''}
          </p>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- imports

function ImportDialog() {
  const A = useApp.getState;
  const [file, setFile] = useState<File | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const close = () => A().closeDialog();
  const addImages = (list: FileList | null) => {
    const arr = Array.from(list || []).filter((f) => /\.(jpe?g|png|webp|gif)$/i.test(f.name));
    setImages((cur) => {
      const m = new Map(cur.map((f) => [f.name.toLowerCase(), f]));
      arr.forEach((f) => m.set(f.name.toLowerCase(), f));
      return [...m.values()].slice(0, 100);
    });
  };
  const dirProps = { webkitdirectory: '', directory: '' } as Record<string, string>;

  return (
    <Modal onClose={close} label="Importēt datus" testId="import-dialog">
      {result ? (
        <div className="dlg">
          <h3>Imports pabeigts</h3>
          <p>
            Ielādētas <b>{result.nodes}</b> jomas un <b>{result.cards}</b> kārtis, pievienoti <b>{result.images}</b> attēli.
          </p>
          {result.missingImages.length ? (
            <div className="imp-miss">
              <p className="muted">Šiem kāršu attēliem netika atrasts fails — tos var pievienot vēlāk kārts redaktorā:</p>
              <ul>
                {result.missingImages.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="dlg-actions">
            <span className="sp" />
            <button type="button" className="btn sm primary" onClick={close} autoFocus>
              Labi
            </button>
          </div>
        </div>
      ) : (
        <form
          className="dlg"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!file) return;
            setBusy(true);
            const r = await A().importData(file, images);
            setBusy(false);
            if (r) setResult(r);
          }}
        >
          <h3>Importēt datus</h3>
          <p className="muted" style={{ margin: 0 }}>
            Ielādē apli no <b>HTML versijas faila</b> (balansa-aplis-….html) vai <b>JSON eksporta</b>. Jomas, saites, enerģijas iestatījumi, kārtis, žurnāls un
            vēsture tiks pārnesti.
          </p>
          <div className="field">
            <label htmlFor="imp-file">Datu fails</label>
            <input id="imp-file" className="inp" type="file" accept=".html,.htm,.json,text/html,application/json" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <div className="field">
            <span className="lbl-t">Kāršu attēli (neobligāti)</span>
            <div className="dlg-row">
              <label className="btn sm">
                Izvēlēties attēlus…
                <input type="file" accept="image/*" multiple hidden onChange={(e) => (addImages(e.target.files), (e.target.value = ''))} data-testid="import-images" />
              </label>
              <label className="btn sm">
                Izvēlēties mapi img…
                <input type="file" multiple hidden {...dirProps} onChange={(e) => (addImages(e.target.files), (e.target.value = ''))} />
              </label>
              {images.length ? (
                <button type="button" className="btn sm ghost" onClick={() => setImages([])}>
                  Notīrīt ({images.length})
                </button>
              ) : null}
            </div>
            <p className="cd-hint">Attēli tiek piemeklēti kārtīm pēc faila nosaukuma (piem. img/sports.jpg ↔ sports.jpg).</p>
          </div>
          <p className="imp-warn">Pašreizējais aplis tiks aizstāts. Ieteicams pirms tam “Eksportēt”.</p>
          <div className="dlg-actions">
            <span className="sp" />
            <button type="button" className="btn sm" onClick={close}>
              Atcelt
            </button>
            <button type="submit" className="btn sm primary" disabled={!file || busy}>
              {busy ? 'Importē…' : 'Importēt un aizstāt'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
